// The commander layer. The player (and the enemy AI) sets a POSTURE for the
// whole army; each team's commander turns that intent into per-squad
// objectives every couple of seconds. Squads are never blindly obedient:
// enemy squads that push into our half get intercepted by whoever is
// closest, regardless of posture — an army that is told to advance still
// refuses to let a column stroll past it toward the King.

import { TICK_RATE } from './constants';
import { squadCentroid, HOME_X } from './squads';
import type { GameState, Posture, SquadObjective, SquadState, Team } from './types';

/** How deep (toward our castle) an enemy squad must be to count as a threat
 * worth peeling someone off for, by posture. Attacking armies only turn back
 * for threats that are genuinely behind their lines. */
const THREAT_DEPTH: Record<Posture, number> = {
  defend: 10,
  rally: 25,
  raid: 40,
  attack: 70,
};

interface Threat {
  squadId: string;
  x: number;
  z: number;
  depth: number;
}

function soldierPop(state: GameState, team: Team): number {
  let n = 0;
  for (const c of Object.values(state.characters)) {
    if (c.alive && c.brain === 'soldier' && c.team === team) n++;
  }
  return n;
}

function setObjective(
  state: GameState,
  squad: SquadState,
  objective: SquadObjective,
  targetX: number,
  targetZ: number,
  targetSquad: string | null,
  changedFlag: { horn: boolean }
): void {
  const was = squad.objective;
  if (was !== objective || squad.targetSquad !== targetSquad) {
    if (objective === 'assault' && was !== 'assault') squad.advanceStep = 0;
    if (!changedFlag.horn) {
      changedFlag.horn = true;
      state.events.push({ type: 'horn', x: squad.anchorX, z: squad.anchorZ, team: squad.team });
    }
  }
  squad.objective = objective;
  squad.targetX = targetX;
  squad.targetZ = targetZ;
  squad.targetSquad = targetSquad;
}

/** Reassess the battlefield and (re)assign every squad an objective. */
export function tickCommander(state: GameState, team: Team): void {
  const ownSign = team === 'friendly' ? -1 : 1; // sign of our castle's X
  const posture = state.posture[team];
  const changed = { horn: false };

  const mySquads: SquadState[] = [];
  for (const squad of Object.values(state.squads)) {
    if (squad.team !== team) continue;
    if (squadCentroid(state, squad.id).count === 0) continue;
    mySquads.push(squad);
  }
  if (mySquads.length === 0) return;

  // enemy squads that have pushed into our territory, deepest first
  const threats: Threat[] = [];
  for (const squad of Object.values(state.squads)) {
    if (squad.team === team) continue;
    const c = squadCentroid(state, squad.id);
    if (c.count === 0) continue;
    const depth = c.x * ownSign; // bigger = closer to our castle
    if (depth > THREAT_DEPTH[posture]) {
      threats.push({ squadId: squad.id, x: c.x, z: c.z, depth });
    }
  }
  threats.sort((a, b) => b.depth - a.depth);

  // closest available squad peels off to meet each threat
  const available = new Set(mySquads);
  for (const threat of threats) {
    if (available.size === 0) break;
    let best: SquadState | null = null;
    let bestD = Infinity;
    for (const squad of available) {
      const d = Math.hypot(squad.anchorX - threat.x, squad.anchorZ - threat.z);
      if (d < bestD) {
        bestD = d;
        best = squad;
      }
    }
    if (!best) break;
    available.delete(best);
    setObjective(state, best, 'intercept', threat.x, threat.z, threat.squadId, changed);
  }

  // everyone else follows the posture
  let holdIndex = 0;
  for (const squad of mySquads) {
    if (!available.has(squad)) continue;
    switch (posture) {
      case 'defend': {
        // arc in front of our own gate, one squad garrisoning the courtyard
        if (holdIndex === 0 && mySquads.length > 2) {
          setObjective(state, squad, 'garrison', ownSign * 180, 0, null, changed);
        } else {
          setObjective(state, squad, 'hold', ownSign * 120, squad.laneZ * 0.9, null, changed);
        }
        holdIndex++;
        break;
      }
      case 'attack':
        setObjective(state, squad, 'assault', squad.targetX, squad.targetZ, null, changed);
        break;
      case 'raid': {
        // sweep the enemy's farmland outside their walls, north and south
        const north = holdIndex % 2 === 0;
        setObjective(state, squad, 'raid', -ownSign * 151, north ? 33 : -33, null, changed);
        holdIndex++;
        break;
      }
      case 'rally':
        setObjective(state, squad, 'rally', ownSign * HOME_X, squad.laneZ, null, changed);
        break;
    }
  }
}

/** The enemy's own high command: picks a posture from the war situation. */
export function enemyPosturePolicy(state: GameState): void {
  const myPop = soldierPop(state, 'enemy');
  const theirPop = soldierPop(state, 'friendly');
  const alarmed = state.tick < state.alarmUntil.enemy;
  let posture: Posture = state.posture.enemy;

  if (state.tick < 35 * TICK_RATE) {
    posture = 'defend';
  } else if (alarmed || myPop < 10) {
    posture = 'defend';
  } else if (myPop >= 16 && myPop >= theirPop * 0.9) {
    posture = 'attack';
  } else if (myPop >= 12 && theirPop > myPop) {
    posture = 'raid';
  } else {
    posture = 'defend';
  }

  if (posture !== state.posture.enemy) {
    state.posture.enemy = posture;
    tickCommander(state, 'enemy');
  }
}
